import React, { useState, useEffect, useRef, useCallback } from 'react';
import './WhatWeOffer.css';

const WhatWeOffer = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [isLocked, setIsLocked] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  const sectionRef = useRef(null);
  const touchStartY = useRef(0);
  const lastScrollTime = useRef(0);

  const offers = [
    {
      id: 'personal-loans',
      title: 'Personal Loans',
      description:
        'Flexible financing for personal goals, planned expenses, and important life moments.',
      amount: 'Based on eligibility',
      features: ['Quick processing', 'Minimal paperwork', 'Flexible terms']
    },
    {
      id: 'business-loans',
      title: 'Business Loans',
      description:
        'Strategic financing to support business growth, expansion, and working capital needs.',
      amount: 'Based on eligibility',
      features: ['Custom terms', 'Quick processing', 'Competitive rates']
    },
    {
      id: 'flexible-repayment',
      title: 'Flexible Repayment',
      description:
        'Repayment options designed to suit different financial needs and repayment capacities.',
      amount: 'Tailored options',
      features: ['Easy eligibility', 'Flexible terms', 'Clear process']
    },
    {
      id: 'digital-disbursal',
      title: 'Digital Disbursal',
      description:
        'Lightning-fast, paperless credit solutions with instant approval and digital documentation.',
      amount: 'Quick processing',
      features: ['Fast approval', '100% digital', 'Paperless process']
    }
  ];

  /* --------------------------------------------------
     BODY SCROLL LOCK (CRITICAL)
  -------------------------------------------------- */

  const lockBodyScroll = () => {
    document.body.style.overflow = 'hidden';
  };

  const unlockBodyScroll = () => {
    document.body.style.overflow = '';
  };

  /* --------------------------------------------------
     SECTION VISIBILITY
  -------------------------------------------------- */

  const isSectionInView = () => {
    if (!sectionRef.current) return false;
    
    const rect = sectionRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const buffer = viewportHeight * 0.2; // 20% buffer
    
    return (
      rect.top <= buffer &&
      rect.bottom >= viewportHeight - buffer
    );
  };

 const handlePageScroll = useCallback(() => {
  const shouldLock = isSectionInView();

  if (shouldLock && !isLocked) {
    lockBodyScroll();
    setIsLocked(true);
  }

  if (!shouldLock && isLocked) {
    unlockBodyScroll();
    setIsLocked(false);
  }
}, [isLocked]);


  /* --------------------------------------------------
     PANEL CHANGE (DEBOUNCED)
  -------------------------------------------------- */

  const changePanel = useCallback(
  (direction) => {
    if (isAnimating) return;

    const newIndex = activeTab + direction;

    if (newIndex < 0 || newIndex >= offers.length) {
      unlockBodyScroll();
      setIsLocked(false);
      return;
    }

    setIsAnimating(true);
    setActiveTab(newIndex);

    setTimeout(() => {
      setIsAnimating(false);
    }, 650);
  },
  [isAnimating, activeTab, offers.length]
);


  /* --------------------------------------------------
     WHEEL (DESKTOP)
  -------------------------------------------------- */

const handleWheel = useCallback(
  (e) => {
    if (!isLocked) return;

    const delta = Math.sign(e.deltaY);

    if (
      (delta > 0 && activeTab < offers.length - 1) ||
      (delta < 0 && activeTab > 0)
    ) {
      e.preventDefault();

      if (Date.now() - lastScrollTime.current > 500) {
        changePanel(delta);
        lastScrollTime.current = Date.now();
      }
    }
  },
  [isLocked, activeTab, offers.length, changePanel]
);

  /* --------------------------------------------------
     TOUCH (MOBILE)
  -------------------------------------------------- */

  const handleTouchStart = (e) => {
    touchStartY.current = e.touches[0].clientY;
  };

 const handleTouchMove = useCallback(
  (e) => {
    if (!isLocked) return;

    const currentY = e.touches[0].clientY;
    const diff = touchStartY.current - currentY;
    const now = Date.now();

    if (Math.abs(diff) < 50 || now - lastScrollTime.current < 800) {
      return;
    }

    if (
      (diff > 0 && activeTab < offers.length - 1) ||
      (diff < 0 && activeTab > 0)
    ) {
      e.preventDefault();

      const direction = diff > 0 ? 1 : -1;
      changePanel(direction);
      lastScrollTime.current = now;
    }
  }, [isLocked, activeTab, offers.length, changePanel]);


  /* --------------------------------------------------
     LISTENERS
  -------------------------------------------------- */

useEffect(() => {
  window.addEventListener('scroll', handlePageScroll, { passive: true });
  window.addEventListener('wheel', handleWheel, { passive: false });
  window.addEventListener('touchstart', handleTouchStart, { passive: true });
  window.addEventListener('touchmove', handleTouchMove, { passive: false });

  return () => {
    unlockBodyScroll();
    window.removeEventListener('scroll', handlePageScroll);
    window.removeEventListener('wheel', handleWheel);
    window.removeEventListener('touchstart', handleTouchStart);
    window.removeEventListener('touchmove', handleTouchMove);
  };
}, [handlePageScroll, handleWheel, handleTouchMove]);


  /* --------------------------------------------------
     RENDER
  -------------------------------------------------- */

  return (
    <section className="what-we-do" ref={sectionRef}>
      <div className="what-wrap">
        <div className="what-header">
          <span className="eyebrow">WHAT WE OFFER</span>
          <h2>Lending that adapts to your needs</h2>
          <p>Financial products designed around your goals, not assumptions.</p>
        </div>

        <div className="offer-split">
          <div className="offer-index">
            {offers.map((offer, index) => (
              <button
                key={offer.id}
                className={`offer-link ${index === activeTab ? 'active' : ''}`}
                onClick={() => setActiveTab(index)}
              >
                {offer.title} <span>{offer.amount}</span>
              </button>
            ))}
          </div>

          <div className="offer-detail">
            {offers.map((offer, index) => (
              <section
                key={offer.id}
                className={`offer-panel ${index === activeTab ? 'active' : ''}`}
              >
                <h3>{offer.title}</h3>
                <p>{offer.description}</p>
                <div className="detail-meta">
                  <span>{offer.amount}</span>
                  {offer.features.map((feature, i) => (
                    <span key={i}>{feature}</span>
                  ))}
                </div>
              </section>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhatWeOffer;
      
